import React, { useEffect, useState } from "react";
import { View, StyleSheet } from "react-native";
import { useForm, Controller } from "react-hook-form";
import { Switch } from "@rneui/themed";
import { DatePickerInput } from "react-native-paper-dates";
import { z } from "zod";

import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import { File, Remove } from "../../../svgs/CustomCoursework";
import FormLayout from "../common/FormLayout";
import {
  ActivityState,
  GetAssignmentDocument,
  StoragePathType,
  useEditAssignmentMutation,
} from "@axio/graphql/generated/graphql";

import { zodResolver } from "@hookform/resolvers/zod";
import WorksheetForm from "../WorksheetForm/WorksheetForm";
import { VIMEO_REGEX, YOUTUBE_REGEX } from "../../../../utils/regex.dictionary";
import FileUploadComponent from "../../../file/FileUploadComponent/FileUploadComponent";
import { ProcessedFile } from "../../../../hooks/useFileUpload";
import CustomRichTextEditor from "../../../TextEditor/CustomRichTextEditor";
import { buildAssignmentActivityData } from "./assignmentForm.utils";

const maybeFormatDueDate = (date: unknown): Date | null => {
  if (!date) return null;
  if (date instanceof Date) return date;
  if (typeof date === "string") {
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

// Helper function to truncate long text
const truncateText = (text, maxLength) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

const schema = z.object({
  activityName: z.string().min(1, "Activity name is required"),
  description: z.string().optional(),
  videoUrl: z.string().optional(),
  files: z
    .array(
      z.object({
        id: z.string(),
        fileName: z.string(),
        fileURL: z.string(),
        contentType: z.string().optional(),
        size: z.number().optional(),
      })
    )
    .optional(),
  dueDate: z.union([z.string(), z.date()]).nullable().optional(),
  enableFileUpload: z.boolean().optional(),
  worksheetId: z.string().nullable().optional(),
  activityState: z.string().optional(),
  scheduledDate: z.union([z.string(), z.date()]).optional(),
});

const AssignmentForm = ({ activityData, onSave, onCancel, error, isSubmitting }) => {
  const [uploadError, setUploadError] = useState(null);
  const [worksheetError, setWorksheetError] = useState(null);
  const [worksheetFormOpen, setWorksheetFormOpen] = useState<"open" | "closed" | "error">("closed");
  const [hasWorksheet, setHasWorksheet] = useState(Boolean(activityData?.worksheet?.id));
  const [removeWorksheetMutation, { loading: isRemovingWorksheet }] = useEditAssignmentMutation();

  const [pdfUrl, setPdfUrl] = useState("");

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      activityName: activityData?.title || "",
      description: activityData?.description || "",
      videoUrl: activityData?.videoUrl || "",
      files: activityData?.files || [],
      dueDate: maybeFormatDueDate(activityData?.dueDate),
      enableFileUpload: activityData?.enableFileUpload ?? true,
      worksheetId: activityData?.worksheet?.id,
      activityState: activityData?.activityState || "draft",
      scheduledDate: activityData?.scheduledDate || undefined,
    },
    resolver: zodResolver(schema),
  });

  const watchFiles = watch("files");
  const watchActivityState = watch("activityState");

  const [activelyEditing, setActivelyEditing] = useState(false);

  useEffect(() => {
    if (activityData?.worksheet) {
      setPdfUrl(activityData.worksheet.files[0]?.fileURL || "");
    }
    setHasWorksheet(Boolean(activityData?.worksheet?.id));
  }, [activityData]);

  // File upload handling
  const handleAfterUpload = async (processedFilesArray: ProcessedFile[]) => {
    const newFiles = [
      ...watchFiles,
      ...processedFilesArray.map((processedFile) => ({
        id: processedFile.file.id,
        fileName: processedFile.file.fileName,
        fileURL: processedFile.file.fileURL,
        contentType: processedFile.mimeType,
        size: processedFile.size,
      })),
    ];
    setValue("files", newFiles);
  };

  // Handle removing a file
  const handleFileRemove = (fileToRemove) => {
    const updatedFiles = watchFiles.filter((file) => file !== fileToRemove);
    setValue("files", updatedFiles);
  };

  const handleAddWorksheet = () => {
    if (isValid) {
      setWorksheetFormOpen("open");
    } else {
      setWorksheetFormOpen("error");
    }
  };

  const clearWorksheetState = () => {
    setValue("worksheetId", null, { shouldDirty: true });
    setPdfUrl("");
    setHasWorksheet(false);
  };

  const handleRemoveWorksheet = async () => {
    setWorksheetError(null);

    if (!activityData?.id || !activityData?.worksheet?.id) {
      clearWorksheetState();
      return;
    }

    try {
      const response = await removeWorksheetMutation({
        variables: {
          id: activityData.id,
          assignment: { worksheetId: null },
        },
        refetchQueries: [{ query: GetAssignmentDocument, variables: { id: activityData.id } }],
        awaitRefetchQueries: true,
      });

      if (!response.data?.editAssignment?.success) {
        setWorksheetError(response.data?.editAssignment?.message || "Failed to remove worksheet");
        return;
      }

      clearWorksheetState();
    } catch {
      setWorksheetError("Failed to remove worksheet");
    }
  };

  // Render file list
  const renderFileList = () => {
    return watchFiles.map((file, index) => (
      <View key={index} style={styles.uploadedFile}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <File />
          <CustomSpacing type="horizontal" size="s" />
          <CustomText size="m" weight="ultraBold" text={truncateText(file.fileName, 40)} useTranslationText={false} />
        </View>

        <CustomButton
          leftIcon={<Remove style={{ paddingRight: 4 }} />}
          text="Remove"
          size="s"
          style={{ backgroundColor: "#D0D5DD", borderRadius: 4 }}
          textStyle={{ fontSize: 14, fontWeight: 700 }}
          onPress={() => handleFileRemove(file)}
        />
      </View>
    ));
  };

  // Form submission
  const handleCreateAssignment = handleSubmit(async (data) => {
    if (!data.activityName.trim()) {
      return;
    }

    setActivelyEditing(false);

    await onSave(buildAssignmentActivityData(data));
  });

  if (worksheetFormOpen === "open") {
    return (
      <WorksheetForm
        onClose={() => setWorksheetFormOpen("closed")}
        activityData={activityData}
        setValue={setValue}
        pdfUrl={pdfUrl}
        setPdfUrl={setPdfUrl}
        onWorksheetGenerated={() => setHasWorksheet(true)}
        hasWorksheet={hasWorksheet}
        onSubmit={handleCreateAssignment}
      />
    );
  }

  return (
    <FormLayout
      title={`${activityData?.id ? "Edit" : "Create"} Assignment`}
      isSubmitting={isSubmitting || isRemovingWorksheet}
      onCancel={onCancel}
      onSubmit={handleCreateAssignment}
      actionText={activityData?.id ? "Save Assignment" : "Create Assignment"}
      error={error || worksheetError}
      isAssignment={true}
      onAddWorksheet={handleAddWorksheet}
      onRemoveWorksheet={hasWorksheet ? handleRemoveWorksheet : null}
      hasWorksheet={hasWorksheet}
      activityData={activityData}>
      <CustomText text="Assignment Name" size="m" weight="ultraBold" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="activityName"
        control={control}
        rules={{ required: true }}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            placeholder="Enter assignment name"
            onBlur={onBlur}
            onChangeText={onChange}
            value={value}
            style={undefined}
          />
        )}
      />
      {errors.activityName && <CustomText text="Activity name is required" style={styles.errorText} />}

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Due Date" size="m" weight="ultraBold" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="dueDate"
        control={control}
        rules={{ required: false }}
        render={({ field: { onChange, value } }) => (
          <DatePickerInput
            locale="en"
            value={value instanceof Date ? value : value ? new Date(value) : undefined}
            onChange={(date) => onChange(date ?? null)}
            onChangeText={(text) => {
              if (!text || text.trim() === "") {
                onChange(null);
              }
            }}
            inputMode="start"
            mode="outlined"
            outlineColor={"#D1D5DB"}
            selectionColor={undefined}
            activeOutlineColor={"blue"}
            withDateFormatInLabel={false}
            placeholder="MM/DD/YYYY"
            theme={{
              colors: {
                primary: "#000000",
                onSurface: "#000000",
              },
              roundness: 8,
            }}
            style={styles.datePicker}
            aria-label="Due Date date picker"
          />
        )}
      />
      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Activity Description" size="m" weight="ultraBold" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="description"
        control={control}
        rules={{ required: false }}
        render={({ field: { onChange, value } }) => {
          const handleDescriptionChange = (content: string) => {
            setActivelyEditing(true);
            onChange(content);
            setValue("description", content, { shouldDirty: true, shouldValidate: false });
          };

          return (
            <CustomRichTextEditor
              value={value}
              onChange={handleDescriptionChange}
              isSubmitting={isSubmitting}
              placeholder="Briefly describe what students will learn in this activity."
              activelyEditing={activelyEditing}
            />
          );
        }}
      />
      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="File Upload" size="m" weight="ultraBold" />
      <CustomText style={styles.subText} text="Upload files students will need for this assignment" />
      <CustomSpacing type="vertical" size="s" />
      <FileUploadComponent
        storagePath={StoragePathType.Resources}
        afterUpload={handleAfterUpload}
        onError={(msg) => setUploadError(msg)}
        multiple={true}
      />
      {uploadError && <CustomText useTranslationText={false} text={uploadError} style={styles.errorText} />}
      {renderFileList()}

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Video URL" size="m" weight="ultraBold" />
      <CustomText style={styles.subText} text="Paste either a YouTube or Vimeo link here" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="videoUrl"
        control={control}
        rules={{
          validate: (value) => {
            if (!value) return true;
            const isYouTube = YOUTUBE_REGEX.test(value.trim());
            const isVimeo = VIMEO_REGEX.test(value.trim());
            return isYouTube || isVimeo || "Please enter a valid YouTube or Vimeo link.";
          },
        }}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            placeholder="Paste either a YouTube or Vimeo link here"
            onBlur={onBlur}
            onChangeText={onChange}
            value={value}
            style={undefined}
          />
        )}
      />

      <CustomSpacing type="vertical" size="xl" />
      <View style={styles.toggleContainer}>
        <CustomText text="Enable student file upload" size="m" weight="ultraBold" />
        <CustomSpacing type="horizontal" size="s" />
        <Controller
          name="enableFileUpload"
          control={control}
          render={({ field: { onChange, value } }) => <Switch value={value} onValueChange={onChange} />}
        />
      </View>

      <CustomSpacing type="vertical" size="m" />
      <View style={styles.activityState}>
        <CustomText text="Publish this Activity" size="l" weight="ultraBold" useTranslationText={false} />
        <CustomText
          text="Publish this activity now or schedule a publish date."
          size="s"
          useTranslationText={false}
          style={styles.stepDescription}
        />
        <Controller
          control={control}
          name="activityState"
          render={({ field: { onChange, value } }) => (
            <div style={{ display: "flex", gap: 10, marginTop: 5 }}>
              {Object.keys(ActivityState).map((state) => (
                <CustomButton
                  key={state}
                  text={ActivityState[state]}
                  size="s"
                  style={[styles.activityStateOption, ActivityState[state] === value && styles.activeState]}
                  onPress={() => {
                    onChange(ActivityState[state]);
                  }}
                />
              ))}
            </div>
          )}
        />
        {watchActivityState === ActivityState.Scheduled && (
          <>
            <CustomText
              text="This Activity will automatically be published after:"
              size="s"
              useTranslationText={false}
              style={styles.stepDescription}
            />
            <Controller
              control={control}
              name="scheduledDate"
              render={({ field: { onChange, value } }) => (
                <div style={{ maxWidth: 250, marginTop: 10 }}>
                  <DatePickerInput
                    locale="en"
                    value={value instanceof Date ? value : value ? new Date(value) : undefined}
                    onChange={onChange}
                    inputMode="start"
                    mode="outlined"
                    outlineColor={"#D1D5DB"}
                    selectionColor={undefined}
                    activeOutlineColor={"blue"}
                    withDateFormatInLabel={false}
                    placeholder="MM/DD/YYYY"
                    theme={{
                      colors: {
                        primary: "#000000",
                        onSurface: "#667085",
                      },
                      roundness: 8,
                    }}
                    style={styles.datePicker}
                    aria-label="Schedule published date picker"
                  />
                </div>
              )}
            />
          </>
        )}
      </View>

      {worksheetFormOpen === "error" && (
        <CustomText
          text="Please fill in all required fields before adding a worksheet."
          style={styles.errorText}
          size="s"
        />
      )}
    </FormLayout>
  );
};

const styles = StyleSheet.create({
  subText: {
    color: "#667085",
    fontSize: 14,
    fontWeight: "400",
  },
  toggleContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  errorText: {
    color: "#FF0000",
    marginTop: 8,
  },
  uploadedFile: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 12,
    backgroundColor: "#F3F4F6",
    borderRadius: 8,
    marginBottom: 16,
    marginTop: 16,
  },
  datePicker: {
    backgroundColor: "#FFFFFF",
    borderColor: "#D5D7DA",
    borderRadius: 10,
    color: "#000000",
    fontSize: 14,
  },
  activityState: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
  },
  activeState: {
    backgroundColor: "#0084FF1A",
    color: "white",
    borderColor: "#0084FF",
  },
  activityStateOption: {
    borderRadius: 30,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderWidth: 2,
    borderStyle: "solid",
    color: "#667085",
    borderColor: "transparent",
  },
  stepDescription: {
    color: "#667085",
    marginTop: 5,
  },
});

export default AssignmentForm;
